import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

interface ClientSelectProps {
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function ClientSelect({ value, onValueChange, placeholder = "Select client", disabled = false }: ClientSelectProps) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const { data: clients, isLoading } = useQuery({
    queryKey: ["clients", "active"],
    queryFn: async () => {
      // Select all columns to include is_active for filtering
      // Note: is_active column exists in DB but not in auto-generated types
      const { data, error } = await supabase
        .from("client")
        .select("*")
        .order("name", { ascending: true });

      if (error) throw error;

      // Filter out archived clients (is_active = false)
      return (data ?? [])
        .filter((client: any) => client.is_active !== false)
        .map((client) => ({ id: client.id, name: client.name }));
    },
    // Only run query when auth is ready to prevent RLS returning empty results
    enabled: isAuthenticated && !authLoading,
  });

  if (isLoading) {
    return <Skeleton className="h-10 w-full" />;
  }

  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger disabled={disabled}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {clients?.map((client) => (
          <SelectItem key={client.id} value={client.id}>
            {client.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
